/**
 * Fade Slideshow Component
 * A slideshow with fade transitions between slides.
 *
 * @attr {string} autoplay - Seconds between slides (enables autoplay when present)
 * @attr {string} initial-slide - Initial slide index (default: 0)
 * @attr {boolean} pause-on-hover - Pause autoplay when hovering over the slideshow
 *
 * @example
 * <fade-slideshow autoplay="4" pause-on-hover initial-slide="0">
 *   <div ref="slides" class="fade-slides">
 *     <div class="fade-slide" data-index="0" data-active>Slide 1</div>
 *     <div class="fade-slide" data-index="1">Slide 2</div>
 *   </div>
 * </fade-slideshow>
 *
 * @fires slideshow:select - Fired when a slide is selected
 */
class FadeSlideshow extends HTMLElement {
  #currentIndex = 0;
  /** @type {number|null} */
  #autoplayInterval = null;
  #isDragging = false;
  #hasDragged = false;
  #startX = 0;
  #startY = 0;
  #deltaX = 0;
  #paused = false;
  #manuallyPaused = false;

  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string|null} oldValue
   * @param {string|null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'initial-slide' && oldValue !== newValue && this.isConnected) {
      const index = Number.parseInt(newValue || '0', 10) || 0;
      this.select(index, { animate: false, userInitiated: false });
    }
  }

  connectedCallback() {
    this.#setupSlides();
    this.#setupDragHandlers();
    if (this.pauseOnHover) {
      this.#setupHoverHandlers();
      this.#setupFocusHandlers();
    }

    const initialSlideAttr = this.getAttribute('initial-slide') || '0';
    const initialSlide = Number.parseInt(initialSlideAttr, 10) || 0;
    this.select(initialSlide, { animate: false, userInitiated: false });

    this.resume();
  }

  disconnectedCallback() {
    this.#stopAutoplay();
    this.#removeDragHandlers();
    this.#removeHoverHandlers();
    this.#removeFocusHandlers();
  }

  get slides() {
    return Array.from(this.querySelectorAll('.fade-slide'));
  }

  get slidesContainer() {
    return this.querySelector('[ref="slides"]') || this.querySelector('.fade-slides');
  }

  get current() {
    return this.#currentIndex;
  }

  get autoplay() {
    return this.hasAttribute('autoplay');
  }

  get pauseOnHover() {
    return this.hasAttribute('pause-on-hover');
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay') || '4';
    const value = Number.parseInt(interval, 10);
    return Number.isNaN(value) ? 4000 : value * 1000;
  }

  get paused() {
    return this.#paused;
  }

  set paused(value) {
    this.#paused = value;
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  #setupSlides() {
    const slides = this.slides;
    slides.forEach((slide, i) => {
      slide.setAttribute('data-index', String(i));
    });
  }

  /**
   * Select a slide by index
   * @param {number} index - The index of the slide to select
   * @param {Object} options - Options
   * @param {boolean} [options.animate=true] - Whether to animate the transition
   * @param {boolean} [options.userInitiated=false] - Whether the selection was user initiated
   */
  select(index, options = { animate: true, userInitiated: false }) {
    const slides = this.slides;
    if (!slides.length) return;

    const { animate = true, userInitiated = false } = options;

    // Wrap around using local variable
    let targetIndex = index;
    if (targetIndex < 0) targetIndex = slides.length - 1;
    if (targetIndex >= slides.length) targetIndex = 0;

    const previousIndex = this.#currentIndex;
    this.#currentIndex = targetIndex;

    // Update slide states
    slides.forEach((slide, i) => {
      if (i === targetIndex) {
        slide.setAttribute('data-active', '');
        slide.setAttribute('aria-hidden', 'false');
      } else {
        slide.removeAttribute('data-active');
        slide.setAttribute('aria-hidden', 'true');
      }
    });

    // Update CSS variable for external use
    this.style.setProperty('--fade-slideshow-current', String(targetIndex));

    // Dispatch event
    this.dispatchEvent(
      new CustomEvent('slideshow:select', {
        bubbles: true,
        detail: {
          index: targetIndex,
          previousIndex,
          userInitiated,
          slide: slides[targetIndex],
        },
      })
    );
  }

  next(options = { animate: true, userInitiated: true }) {
    this.select(this.#currentIndex + 1, options);
  }

  previous(options = { animate: true, userInitiated: true }) {
    this.select(this.#currentIndex - 1, options);
  }

  play() {
    if (this.#autoplayInterval) return;

    this.paused = false;
    this.#manuallyPaused = false;

    this.#autoplayInterval = window.setInterval(() => {
      // Skip if document is hidden (background tab)
      if (document.hidden) return;
      this.next({ animate: true, userInitiated: false });
    }, this.autoplayInterval);
  }

  pause() {
    this.paused = true;
    this.#manuallyPaused = true;
    this.#stopAutoplay();
  }

  suspend() {
    this.#stopAutoplay();
  }

  resume() {
    if (!this.autoplay || this.#manuallyPaused) return;
    this.play();
  }

  #stopAutoplay() {
    if (this.#autoplayInterval) {
      window.clearInterval(this.#autoplayInterval);
      this.#autoplayInterval = null;
    }
  }

  // Drag handlers
  /**
   * @param {MouseEvent|TouchEvent} e
   */
  #handleDragStart = (e) => {
    if (this.slides.length <= 1) return;

    this.#isDragging = true;
    this.#hasDragged = false;

    if ('touches' in e && e.touches[0]) {
      this.#startX = e.touches[0].clientX;
      this.#startY = e.touches[0].clientY;
    } else if ('clientX' in e) {
      this.#startX = e.clientX;
      this.#startY = e.clientY;
    }

    this.#deltaX = 0;

    this.suspend();
    this.setAttribute('dragging', '');
  };

  /**
   * @param {MouseEvent|TouchEvent} e
   */
  #handleDragMove = (e) => {
    if (!this.#isDragging) return;

    let currentX = this.#startX;
    let currentY = this.#startY;

    if ('touches' in e && e.touches[0]) {
      currentX = e.touches[0].clientX;
      currentY = e.touches[0].clientY;
    } else if ('clientX' in e) {
      currentX = e.clientX;
      currentY = e.clientY;
    }

    this.#deltaX = currentX - this.#startX;
    const deltaY = currentY - this.#startY;

    if (Math.abs(this.#deltaX) > 10 || Math.abs(deltaY) > 10) {
      this.#hasDragged = true;
    }

    // Prevent vertical scroll if horizontal drag
    if (Math.abs(this.#deltaX) > Math.abs(deltaY)) {
      e.preventDefault();
    }
  };

  #handleDragEnd = () => {
    if (!this.#isDragging) return;

    this.#isDragging = false;
    this.removeAttribute('dragging');

    const SWIPE_THRESHOLD = 50;

    if (this.#hasDragged) {
      if (this.#deltaX > SWIPE_THRESHOLD) {
        this.previous();
      } else if (this.#deltaX < -SWIPE_THRESHOLD) {
        this.next();
      }
    }

    this.#hasDragged = false;
    this.resume();
  };

  #setupDragHandlers() {
    const container = this.slidesContainer;
    if (!container) return;

    container.addEventListener('mousedown', this.#handleDragStart);
    container.addEventListener('mousemove', this.#handleDragMove);
    container.addEventListener('mouseup', this.#handleDragEnd);
    container.addEventListener('mouseleave', this.#handleDragEnd);
    container.addEventListener('touchstart', this.#handleDragStart, { passive: true });
    container.addEventListener('touchmove', this.#handleDragMove, { passive: false });
    container.addEventListener('touchend', this.#handleDragEnd);
  }

  #removeDragHandlers() {
    const container = this.slidesContainer;
    if (!container) return;

    container.removeEventListener('mousedown', this.#handleDragStart);
    container.removeEventListener('mousemove', this.#handleDragMove);
    container.removeEventListener('mouseup', this.#handleDragEnd);
    container.removeEventListener('mouseleave', this.#handleDragEnd);
    container.removeEventListener('touchstart', this.#handleDragStart);
    container.removeEventListener('touchmove', this.#handleDragMove);
    container.removeEventListener('touchend', this.#handleDragEnd);
  }

  #handleMouseEnter = () => {
    this.suspend();
  };

  #handleMouseLeave = () => {
    this.resume();
  };

  #handleFocusIn = () => {
    this.suspend();
  };

  /**
   * @param {FocusEvent} event
   */
  #handleFocusOut = (event) => {
    const nextFocused = event.relatedTarget;
    if (nextFocused instanceof Node && this.contains(nextFocused)) return;
    this.resume();
  };

  #setupHoverHandlers() {
    this.addEventListener('mouseenter', this.#handleMouseEnter);
    this.addEventListener('mouseleave', this.#handleMouseLeave);
  }

  #removeHoverHandlers() {
    this.removeEventListener('mouseenter', this.#handleMouseEnter);
    this.removeEventListener('mouseleave', this.#handleMouseLeave);
  }

  #setupFocusHandlers() {
    this.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('focusout', this.#handleFocusOut);
  }

  #removeFocusHandlers() {
    this.removeEventListener('focusin', this.#handleFocusIn);
    this.removeEventListener('focusout', this.#handleFocusOut);
  }
}

if (!customElements.get('fade-slideshow')) {
  customElements.define('fade-slideshow', FadeSlideshow);
}
